/**
 * MapView Component - Interactive Leaflet map
 */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { useProjectStore, usePoleStore, useCableStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import { useMapControlsStore } from '../../store/mapControlsStore';
import { useAuthStore } from '../../stores/authStore';
import { useRezimPrace } from '../../store/roleViewStore';
import { Pole, Cable, ONUWithDetails } from '../../types';
import { getFiberHexByNumber } from '../../utils/colorUtils';
import { findSplitterType } from '../../utils/splitterCatalog';
import { AddPoleDialog } from '../Dialogs/AddPoleDialog';
import { AddCableDialog } from '../Dialogs/AddCableDialog';
import { PoleReserveDialog } from '../Dialogs/PoleReserveDialog';
import { EditPoleDialog } from '../Dialogs/EditPoleDialog';
import { EditONUDialog } from '../Dialogs/EditONUDialog';
import { CassetteEditorDialog } from '../Dialogs/CassetteEditorDialog';
import { OLTPortConfigDialog } from '../Dialogs/OLTPortConfigDialog';
import { TechnicalReportDialog } from '../Dialogs/TechnicalReportDialog';
import { PlavajuceMenu } from './PlavajuceMenu';
import { ONUTechnicalReportDialog } from '../Dialogs/ONUTechnicalReportDialog';
import { ImportPolesDialog } from '../Dialogs/ImportPolesDialog';
import { ExportPolesDialog } from '../Dialogs/ExportPolesDialog';
import { CableFiberUsageDialog } from '../Dialogs/CableFiberUsageDialog';
import { ProjectFiberUsageDialog } from '../Dialogs/ProjectFiberUsageDialog';
import { InstallationTechDialog } from '../Dialogs/InstallationTechDialog';
import { PoleNotesDialog } from '../Dialogs/PoleNotesDialog';
import ConfirmDeleteDialog from '../Dialogs/ConfirmDeleteDialog';
import UndoToast from '../Toast/UndoToast';
import { CablePopup } from './CablePopup';
import { MapControlsMenu } from './MapControlsMenu';
import { AddCableByClickPanel } from './AddCableByClickPanel';
import { getErrorMessage } from '../../utils/errorMessages';
import { cablesAPI } from '../../services/api';
import { api } from '../../services/api';
import './MapView.css';

// Fix Leaflet default icon issue with webpack
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { cableTypeFromFiberCount } from '../../utils/fiberColors';
import { zoradPodlaOlt, koniecOdOlt } from '../../utils/cableDirection';
import { signalNaStlpe, SIGNAL_FARBY, SignalInfo, SignalStav, prahy } from '../../utils/signalStatus';
import { SignalLegend } from './SignalLegend';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

// Calculate checklist completion status for a pole
const getChecklistStatus = (pole: Pole, allPoles: Pole[]): { percentage: number; borderColor: string } => {
  const checklist = pole.installation_checklist || {};
  const customTasks = (pole as any).custom_tasks || {};
  const taskIds = Object.keys(checklist);
  const customTaskIds = Object.keys(customTasks);

  // Get cassette (check both main pole and virtual pole)
  const virtualPole = allPoles.find(p => p.is_virtual && p.parent_pole_id === pole.id);
  const cassette = virtualPole?.cassette || pole.cassette;

  // If no cassette, check if there are custom tasks
  if (!cassette) {
    if (customTaskIds.length === 0) {
      return { percentage: 0, borderColor: 'white' }; // No checklist and no custom tasks = white border
    }

    // Only custom tasks - calculate completion based on custom tasks
    const completedCustomTasks = customTaskIds.filter(id => customTasks[id].completed === true).length;
    const percentage = (completedCustomTasks / customTaskIds.length) * 100;

    let borderColor = 'white';
    if (percentage === 0) {
      borderColor = '#9e9e9e'; // Gray - not started
    } else if (percentage === 100) {
      borderColor = '#4caf50'; // Green - completed
    } else {
      borderColor = '#ff9800'; // Orange - in progress
    }

    return { percentage, borderColor };
  }

  if (taskIds.length === 0 && customTaskIds.length === 0) {
    return { percentage: 0, borderColor: 'white' }; // No checklist = white border
  }

  // Filter out tasks that cannot be completed (disconnected ports and ports to other splitters)
  const completableTasks = taskIds.filter(taskId => {
    // Check if this is a splitter output task
    const match = taskId.match(/^splitter_(\d+)_output_(\d+)$/);
    if (match) {
      const splitterIndex = parseInt(match[1]);
      const portIndex = parseInt(match[2]);

      const splitter = cassette.splitters?.[splitterIndex];
      if (!splitter) return true; // Keep task if splitter not found

      const output = splitter.outputs?.[portIndex];

      // Exclude disconnected ports (no cable_id)
      if (!output?.cable_id) {
        return false;
      }

      // Exclude ports connected to other splitters
      if (cassette.splitters) {
        const isConnectedToSplitter = cassette.splitters.some((otherSplitter, otherIndex) => {
          return otherIndex !== splitterIndex &&
                 otherSplitter.input_source_type === 'splitter_output' &&
                 otherSplitter.input_splitter_index === splitterIndex &&
                 otherSplitter.input_splitter_output === portIndex;
        });

        if (isConnectedToSplitter) {
          return false;
        }
      }
    }

    return true; // Keep all other tasks (splices, splitter inputs, etc.)
  });

  // Include custom tasks in total count
  const totalCompletableTasks = completableTasks.length + customTaskIds.length;

  if (totalCompletableTasks === 0) {
    return { percentage: 0, borderColor: 'white' };
  }

  const completedChecklistCount = completableTasks.filter(id => checklist[id] === true).length;
  const completedCustomTasksCount = customTaskIds.filter(id => customTasks[id].completed === true).length;
  const totalCompletedCount = completedChecklistCount + completedCustomTasksCount;

  const percentage = (totalCompletedCount / totalCompletableTasks) * 100;

  let borderColor = 'white';
  if (percentage === 0) {
    borderColor = '#9e9e9e'; // Gray - not started
  } else if (percentage === 100) {
    borderColor = '#4caf50'; // Green - completed
  } else {
    borderColor = '#ff9800'; // Orange - in progress
  }

  console.log(`[MapView] Pole ${pole.name} checklist:`, {
    totalTasks: taskIds.length,
    completableTasks: completableTasks.length,
    customTasks: customTaskIds.length,
    completedChecklist: completedChecklistCount,
    completedCustom: completedCustomTasksCount,
    totalCompleted: totalCompletedCount,
    percentage,
    borderColor,
    checklist,
    customTasksData: customTasks
  });

  return { percentage, borderColor };
};

/** Podporný stĺp: bez kazety a bez splittrov — kábel cezeň len prechádza. */
const jePodporny = (pole: Pole) =>
  pole.cassette?.has_cassette === false && !(pole.cassette?.splitters?.length);

// Custom icons for different pole types
const createPoleIcon = (
  pole: Pole,
  allPoles: Pole[],
  isFirstInChain: boolean = false,
  /** farba vonkajšieho prsteňa so stavom signálu; null = nekresliť */
  farbaSignalu: string | null = null,
) => {
  const defaultColors: Record<string, string> = {
    olt: '#e74c3c',           // Red
    splitter_point: '#f39c12', // Orange
    building: '#3498db',       // Blue
    pole: '#95a5a6',           // Gray
  };

  // Check if pole has cassette with splitters
  let color = defaultColors[pole.pole_type] || defaultColors.pole;
  let isMasterSplitter = false;

  // First, check if backbone cassette (main pole) has master splitter - this determines SHAPE
  if (pole.cassette?.splitters && pole.cassette.splitters.length > 0) {
    isMasterSplitter = pole.cassette.splitters.some(s => s.is_master);
  }

  // Calculate FREE customer ports from customer splitters
  // IMPORTANT: Only count ports that go to customers, NOT ports that go to other splitters
  let freeCustomerPorts = 0;

  // Find virtual pole (customer cassette) - will be reused for color logic
  const virtualPole = allPoles.find(p => p.is_virtual && p.parent_pole_id === pole.id);

  // Helper function to count FREE customer ports (excluding ports to other splitters and occupied ports)
  const countFreeCustomerPorts = (splitters: any[]) => {
    let count = 0;
    splitters.forEach((splitter, index) => {
      if (splitter.is_customer_splitter) {
        // Extract total port count from splitter type (e.g., "1:4" -> 4)
        const match = splitter.type.match(/1:(\d+)/);
        if (match) {
          const totalPorts = parseInt(match[1]);

          // Count how many ports go to other splitters
          let portsToOtherSplitters = 0;
          splitters.forEach((otherSplitter, otherIndex) => {
            if (otherIndex !== index &&
                otherSplitter.input_source_type === 'splitter_output' &&
                otherSplitter.input_splitter_index === index) {
              portsToOtherSplitters++;
            }
          });

          // Count occupied customer ports (ports with customer_name)
          let occupiedPorts = 0;
          const outputs = splitter.outputs || [];
          outputs.forEach((output: any) => {
            if (output && output.customer_name) {
              occupiedPorts++;
            }
          });

          // Free ports = total ports - ports to other splitters - occupied customer ports
          const freePorts = totalPorts - portsToOtherSplitters - occupiedPorts;
          count += freePorts;
        }
      }
    });
    return count;
  };

  // Check virtual pole for customer splitters
  if (virtualPole?.cassette?.splitters) {
    freeCustomerPorts += countFreeCustomerPorts(virtualPole.cassette.splitters);
  }

  // Also check main cassette for customer splitters
  if (pole.cassette?.splitters) {
    freeCustomerPorts += countFreeCustomerPorts(pole.cassette.splitters);
  }

  // PRIORITY 0: Check if this pole has a virtual pole (customer cassette) and use its color
  if (virtualPole?.cassette?.splitters && virtualPole.cassette.splitters.length > 0) {
    // Use color from customer cassette (virtual pole)
    const colorSplitter =
      virtualPole.cassette.splitters.find(s => s.determines_pole_color && !!s.color) ||
      virtualPole.cassette.splitters.find(s => !!s.color) ||
      virtualPole.cassette.splitters[0];

    if (colorSplitter?.color) {
      color = colorSplitter.color;
    }
  }
  // PRIORITY 1: Use map_color if inherited from master splitter (set by backend)
  else if (pole.map_color) {
    color = pole.map_color;
  }
  // PRIORITY 2: Use cassette splitter colors (local configuration)
  else if (pole.cassette?.splitters && pole.cassette.splitters.length > 0) {
    // Prefer a splitter explicitly marked to determine color AND with a color,
    // otherwise any splitter that has a color, otherwise fall back to the first splitter
    const colorSplitter =
      pole.cassette.splitters.find(s => s.determines_pole_color && !!s.color) ||
      pole.cassette.splitters.find(s => !!s.color) ||
      pole.cassette.splitters[0];

    if (colorSplitter?.color) {
      color = colorSplitter.color;
    }
  }

  // ASYMETRICKÝ SPLITTER — farba stĺpika podľa VSTUPNÉHO vlákna.
  // - ak má splitter zapnuté color_by_input_fiber → prepíše aj nadradenú farbu
  // - inak sa použije len ako fallback, keď stĺpik nemá inú farbu
  {
    const allSplittersForColor = [
      ...(pole.cassette?.splitters || []),
      ...(virtualPole?.cassette?.splitters || []),
    ];
    const asymSplitter = allSplittersForColor.find(
      (s: any) => findSplitterType(s.type)?.is_symmetric === false && s.input_fiber
    );
    if (asymSplitter?.input_fiber) {
      const fiberHex = getFiberHexByNumber(asymSplitter.input_fiber);
      const noColorSet = color === (defaultColors[pole.pole_type] || defaultColors.pole);
      if (fiberHex && ((asymSplitter as any).color_by_input_fiber || noColorSet)) {
        color = fiberHex;
      }
    }
  }

  // Get checklist status for border color
  const checklistStatus = getChecklistStatus(pole, allPoles);
  let borderColor = checklistStatus.borderColor;

  // Override border color if this is the first pole in cable chain
  if (isFirstInChain) {
    borderColor = '#fbbf24'; // Yellow for first pole in chain
  }

  // Vonkajší prsteň so stavom signálu. Cez box-shadow, aby neprepísal outline,
  // ktorý drží stav inštalačného checklistu — to sú dve rôzne informácie.
  // Tenká tmavá linka medzi nimi ich opticky oddelí.
  const prstenSignalu = farbaSignalu
    ? `0 0 0 4px rgba(0,0,0,0.25), 0 0 0 8px ${farbaSignalu}, `
    : '';

  // Podporný stĺp (bez kazety, kábel cezeň len prechádza) — malá sivá bodka
  // bez portov, aby mapa s desiatkami priechodných stĺpov ostala čitateľná.
  // Ostatné stĺpy sa kreslia ako doteraz.
  if (jePodporny(pole)) {
    return L.divIcon({
      className: 'custom-pole-icon',
      html: `<div style="
        width: 8px; height: 8px; border-radius: 50%;
        background: #7f8c8d; border: 1px solid white;
        box-shadow: ${prstenSignalu}0 1px 3px rgba(0,0,0,0.4);
      "></div>`,
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    });
  }

  // Use square icon for master splitter, circle for others
  const iconHtml = isMasterSplitter
    ? `
      <div style="
        background-color: ${color};
        width: 18px;
        height: 18px;
        border: 2px solid white;
        outline: 3px solid ${borderColor};
        box-shadow: ${prstenSignalu}0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 900;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      ">${freeCustomerPorts > 0 ? freeCustomerPorts : ''}</div>
    `
    : `
      <div style="
        background-color: ${color};
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid white;
        outline: 3px solid ${borderColor};
        box-shadow: ${prstenSignalu}0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 900;
        color: white;
        text-shadow: 0 1px 2px rgba(0,0,0,0.5);
      ">${freeCustomerPorts > 0 ? freeCustomerPorts : ''}</div>
    `;

  return L.divIcon({
    className: 'custom-pole-icon',
    html: iconHtml,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

// Custom icon for ONU
const createONUIcon = (onu: ONUWithDetails) => {
  const statusColors: Record<string, string> = {
    active: '#10b981',    // Green
    pending: '#f59e0b',   // Orange
    offline: '#ef4444',   // Red
    inactive: '#6b7280',  // Gray
  };

  const color = statusColors[onu.status] || statusColors.inactive;

  const iconHtml = `
    <div style="
      background-color: ${color};
      width: 16px;
      height: 16px;
      border-radius: 3px;
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      color: white;
      font-weight: bold;
    ">📡</div>
  `;

  return L.divIcon({
    className: 'custom-onu-icon',
    html: iconHtml,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
};

// Map click handler component
const MapClickHandler: React.FC<{
  onMapClick: (lat: number, lng: number) => void;
}> = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      console.log('🎯 MapClickHandler: click event', e.latlng);
      console.log('🎯 Original event:', e.originalEvent);
      console.log('🎯 Target:', e.originalEvent?.target);
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Auto-fit bounds component - only runs once when poles are first loaded
const AutoFitBounds: React.FC<{ poles: Pole[] }> = ({ poles }) => {
  const map = useMap();
  const hasInitialized = React.useRef(false);

  useEffect(() => {
    // Only fit bounds once when poles are first loaded
    if (poles.length > 0 && !hasInitialized.current) {
      const bounds = poles
        .filter(p => p.latitude && p.longitude)
        .map(p => {
          const lat = typeof p.latitude === 'string' ? parseFloat(p.latitude) : p.latitude!;
          const lng = typeof p.longitude === 'string' ? parseFloat(p.longitude as string) : p.longitude!;
          return [lat, lng] as [number, number];
        });

      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50] });
        hasInitialized.current = true;
      }
    }
  }, [poles, map]);

  return null;
};

// Zoom to target component - zooms to specific location when zoomTarget is set
const ZoomToTarget: React.FC = () => {
  const map = useMap();
  const { zoomTarget, setZoomTarget } = usePoleStore();

  useEffect(() => {
    if (zoomTarget) {
      // Fly to target location with animation
      map.flyTo([zoomTarget.lat, zoomTarget.lng], zoomTarget.zoom, {
        duration: 1.5, // Animation duration in seconds
        easeLinearity: 0.25
      });

      // Clear zoom target after animation
      setTimeout(() => {
        setZoomTarget(null);
      }, 1600);
    }
  }, [zoomTarget, map, setZoomTarget]);

  return null;
};

export const MapView: React.FC = () => {
  const { currentProject, projects, setCurrentProject, canEdit, canDelete } = useProjectStore();
  const { poles, selectedPole, setSelectedPole, fetchPoles, updatePole, deletePole, zoomTarget,
    otvorKazetuPreStlp, setOtvorKazetuPreStlp } = usePoleStore();
  const { cables, selectedCable, setSelectedCable, fetchCables } = useCableStore();
  const { onus, fetchONUs } = useONUStore();
  const { addPoleMode, toggleAddPoleMode, showONUs, toggleShowONUs, showVirtualPoles, toggleShowVirtualPoles,
    showSignal, toggleShowSignal, addCableByClickMode, toggleAddCableByClickMode, setAddCableByClickMode, deleteCableMode, toggleDeleteCableMode, setDeleteCableMode } = useMapControlsStore();
  const { user, hasModule, isAdmin } = useAuthStore();
  const { jePripajanie } = useRezimPrace();

  // Čo sa stane po kliknutí na stĺp, riadi režim práce z hlavičky:
  // zváranie → technická správa s kazetou, pripájanie → montážny dialóg s
  // voľnými portami. Pre admina je to zároveň ten starý „pohľad montážnika",
  // pre technika je to jeho vlastná voľba, čo dnes robí.
  const akoMontaznik = user?.role === 'install_tech' || jePripajanie;

  const [mapCenter, setMapCenter] = useState<[number, number]>([48.1486, 17.1077]); // Bratislava
  const [mapZoom, setMapZoom] = useState(13);
  const [showAddPoleDialog, setShowAddPoleDialog] = useState(false);
  const [showAddCableDialog, setShowAddCableDialog] = useState(false);
  const [showEditPoleDialog, setShowEditPoleDialog] = useState(false);
  const [showEditONUDialog, setShowEditONUDialog] = useState(false);
  const [showONUTechnicalReport, setShowONUTechnicalReport] = useState(false);
  const [showCassetteEditor, setShowCassetteEditor] = useState(false);
  const [showOLTPortConfig, setShowOLTPortConfig] = useState(false);
  const [showTechnicalReport, setShowTechnicalReport] = useState(false);
  const [showInstallationTechDialog, setShowInstallationTechDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showFiberUsageDialog, setShowFiberUsageDialog] = useState(false);
  const [showProjectFiberUsageDialog, setShowProjectFiberUsageDialog] = useState(false);
  const [showPoleNotesDialog, setShowPoleNotesDialog] = useState(false);
  const [fiberUsageCableId, setFiberUsageCableId] = useState<number | null>(null);
  const [selectedONU, setSelectedONU] = useState<ONUWithDetails | null>(null);
  const [clickedPosition, setClickedPosition] = useState<[number, number] | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    type: 'pole' | 'cable' | 'onu';
    pole?: Pole;
    cable?: Cable;
    onu?: ONUWithDetails;
    x: number;
    y: number;
  } | null>(null);
  const [draggingPole, setDraggingPole] = useState<Pole | null>(null);
  const [draggingONU, setDraggingONU] = useState<ONUWithDetails | null>(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [editPoleInitialTab, setEditPoleInitialTab] = useState<'basic' | 'edit' | 'photos' | 'cassette'>('basic');

  // Confirm delete dialog state
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [poleToDelete, setPoleToDelete] = useState<Pole | null>(null);

  // Undo toast state
  const [showUndoToast, setShowUndoToast] = useState(false);
  const [undoMessage, setUndoMessage] = useState('');
  const [undoAuditLogId, setUndoAuditLogId] = useState<number | null>(null);

  // Cable by click mode state
  const [cableChain, setCableChain] = useState<Pole[]>([]);
  // Čo presne vzniklo pri každom kliknutí — bez toho by sa „Späť" nemalo čoho chytiť.
  const [chainSteps, setChainSteps] = useState<
    { cableId: number; fromPoleId: number; toPoleId: number }[]
  >([]);
  // Nastavenia kábla sa po stlačení „Začať ťahať" zbalia do lišty, nech je mapa voľná.
  const [cableByClickCollapsed, setCableByClickCollapsed] = useState(false);
  const [cableByClickParams, setCableByClickParams] = useState({
    fiberCount: 24,
    cableType: 'single-mode',
    cassetteType: 'passthrough' as 'none' | 'passthrough' | 'splitter_1_2' | 'splitter_1_4' | 'splitter_1_8',
    bufferSize: null as 12 | 24 | null, // null = bez bufrov
    bufferColors: [] as string[],
    // true = z celej trasy bude jeden kábel a medzistĺpy budú podporné.
    // Zlučuje sa až pri „Dokončiť", nie po každom kliknutí — počas ťahania tak
    // zostáva každý úsek samostatný a tlačidlo „Späť" funguje ako predtým.
    jedenKabel: false,
  });

  // Delete cable mode state
  const [selectedCablesForDeletion, setSelectedCablesForDeletion] = useState<number[]>([]);

  // Uchytávanie kábla na podporný stĺp: po zapnutí čaká na klik na stĺp.
  // Pridané 2026-09-30.
  const [uchytitNaStlpCableId, setUchytitNaStlpCableId] = useState<number | null>(null);
  // Návin na stĺpe — otvára sa z kontextového menu stĺpa.
  const [navinPoleId, setNavinPoleId] = useState<number | null>(null);

  /**
   * Stav signálu na zákazníckych splittroch — podklad pre farebný prstenec.
   * Počíta sa raz pre celú mapu a drží sa, kým sa nezmenia stĺpy, káble alebo
   * nastavenia projektu. Jedno trasovanie trvá ~0,2 ms, takže aj pri stovke
   * splitterov je to okolo 20 ms.
   */
  const signalPodlaStlpa = useMemo(() => {
    const mapa = new Map<number, SignalInfo>();
    if (!showSignal) return mapa;
    poles.forEach((p) => {
      if (p.is_virtual) return;
      const info = signalNaStlpe(p, poles, cables, currentProject?.settings);
      if (info.stav !== 'ziadny') mapa.set(p.id, info);
    });
    return mapa;
  }, [poles, cables, showSignal, currentProject?.settings]);

  const pocetyStavov = useMemo(() => {
    const p: Record<SignalStav, number> = {
      ziadny: 0, nezapojene: 0, prerusene: 0, slaby: 0, dobry: 0 };
    signalPodlaStlpa.forEach((info: SignalInfo) => { p[info.stav] += 1; });
    return p;
  }, [signalPodlaStlpa]);

  // Fetch poles, cables and ONUs when project changes
  useEffect(() => {
    if (currentProject) {
      fetchPoles(currentProject.id);
      fetchCables(currentProject.id);
      fetchONUs(currentProject.id);
    }
  }, [currentProject, fetchPoles, fetchCables, fetchONUs]);

  // Escape zruší čakanie na klik na podporný stĺp.
  useEffect(() => {
    if (uchytitNaStlpCableId === null) return;
    const naKlavesu = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUchytitNaStlpCableId(null);
    };
    document.addEventListener('keydown', naKlavesu);
    return () => document.removeEventListener('keydown', naKlavesu);
  }, [uchytitNaStlpCableId]);

  // Požiadavka z hľadania zákazníka: otvor kazetu tohto stĺpika. Prichádza ešte
  // predtým, než sú stĺpy načítané (mohol sa meniť projekt), preto sa čaká na
  // ne — a keď dorazia, otvorí sa ten istý dialóg ako pri kliknutí na stĺp.
  useEffect(() => {
    if (!otvorKazetuPreStlp) return;
    const stlp = poles.find((p) => p.id === otvorKazetuPreStlp);
    if (!stlp) return;
    setSelectedPole(stlp);
    if (akoMontaznik) {
      setShowInstallationTechDialog(true);
    } else {
      setShowTechnicalReport(true);
    }
    setOtvorKazetuPreStlp(null);
  }, [otvorKazetuPreStlp, poles, akoMontaznik, setSelectedPole, setOtvorKazetuPreStlp]);

  // Po zapnutí režimu sa vždy začína nastaveniami; po vypnutí nech nezostane
  // visieť rozrobený reťazec, ktorý by sa pri ďalšom zapnutí tváril ako platný.
  useEffect(() => {
    if (addCableByClickMode) {
      setCableByClickCollapsed(false);
    } else {
      setCableChain([]);
      setChainSteps([]);
    }
  }, [addCableByClickMode]);

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleRecalculateColors = async () => {
    if (!currentProject) {
      alert('Nie je vybraný žiadny projekt.');
      return;
    }

    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    if (!window.confirm('Prepočítať farby všetkých splitterov v projekte? Toto môže trvať niekoľko sekúnd.')) {
      return;
    }

    try {
      const response = await api.projects.recalculateColors(currentProject.id);
      console.log('[RecalculateColors] Response:', response);

      // Refresh poles to get updated colors
      await fetchPoles(currentProject.id);

      alert(`✅ Farby prepočítané! Aktualizovaných stĺpikov: ${response.data.updated_count}`);
    } catch (error: any) {
      console.error('[RecalculateColors] Error:', error);
      alert(getErrorMessage(error, 'Nepodarilo sa prepočítať farby.'));
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    console.log('🗺️ Map clicked at:', lat, lng);

    // If cable-by-click mode is active, reset chain
    if (addCableByClickMode) {
      handleMapClickInCableMode();
      return;
    }

    // Only add pole if addPoleMode is enabled
    if (!addPoleMode) {
      console.log('❌ Add pole mode is disabled');
      return;
    }

    if (!currentProject) {
      console.log('❌ No current project');
      return;
    }

    // Check if user can edit
    if (!canEdit()) {
      console.log('❌ User cannot edit this project');
      alert('Nemáte oprávnenie na úpravu tohto projektu. Potrebujete práva Editor alebo Owner.');
      return;
    }

    console.log('✅ Setting clicked position and opening dialog');
    setClickedPosition([lat, lng]);
    setShowAddPoleDialog(true);
  };

  const handlePoleClick = (pole: Pole) => {
    // If cable-by-click mode is active, handle cable creation
    if (addCableByClickMode) {
      handlePoleClickForCable(pole);
      return;
    }

    if (uchytitNaStlpCableId !== null) {
      uchytKabelNaStlp(uchytitNaStlpCableId, pole);
      return;
    }

    setSelectedPole(pole);

    // Check if user is installation technician - show simplified dialog
    if (akoMontaznik) {
      setShowInstallationTechDialog(true);
    } else {
      // Service tech, engineer, admin - show full technical report
      setShowTechnicalReport(true);
    }
  };

  // Uchytenie kábla na podporný stĺp. Kábel sa NEDELÍ — dostane len ďalší
  // bod na trase, takže kazetové väzby aj optická cesta ostávajú nedotknuté.
  const uchytKabelNaStlp = async (cableId: number, pole: Pole) => {
    if (!currentProject) return;
    try {
      const odpoved: any = await cablesAPI.attachSupportPole(currentProject.id, cableId, pole.id);
      const dlzka = odpoved?.data?.length_meters;
      setUchytitNaStlpCableId(null);
      await fetchCables(currentProject.id);
      if (dlzka != null) {
        console.log(`✅ Kábel #${cableId} uchytený na ${pole.name}, nová dĺžka ${dlzka} m`);
      }
    } catch (err: any) {
      setUchytitNaStlpCableId(null);
      alert(getErrorMessage(err, 'Kábel sa nepodarilo uchytiť na stĺp.'));
    }
  };

  // Handle pole click in cable-by-click mode
  const handlePoleClickForCable = async (pole: Pole) => {
    if (!currentProject || !canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    try {
      if (cableChain.length === 0) {
        // First pole - start chain
        setCableChain([pole]);
        console.log('🔗 Cable chain started at pole:', pole.name);
      } else {
        // Second+ pole - create cable and cassette
        const lastPole = cableChain[cableChain.length - 1];

        // Don't create cable to the same pole
        if (lastPole.id === pole.id) {
          alert('Nemôžeš vytvoriť kábel k tomu istému stĺpiku!');
          return;
        }

        console.log(`🔗 Creating cable ${lastPole.name} → ${pole.name}`);

        // Create buffer configuration (only if bufferSize is set)
        const { fiberCount, bufferSize, bufferColors } = cableByClickParams;
        let bufferConfig = undefined;

        if (bufferSize !== null) {
          const bufferCount = Math.ceil(fiberCount / bufferSize);
          const buffers = Array.from({ length: bufferCount }, (_, i) => ({
            index: i + 1,
            color: bufferColors[i] || ['Modrá', 'Oranžová', 'Zelená', 'Hnedá', 'Šedá', 'Biela', 'Červená', 'Čierna', 'Žltá', 'Fialová', 'Ružová', 'Tyrkysová'][i % 12],
          }));
          bufferConfig = {
            buffer_size: bufferSize,
            buffers,
          };
        }

        // Create cable
        // cable_type nesie POČET VLÁKIEN ("24-fiber") — pozor, premenná
        // cableByClickParams.cableType je niečo iné (single-mode / multi-mode).
        // Kým sa cable_type neposielal, backend dopĺňal default 12-fiber a kábel
        // vznikol 12-vláknový bez ohľadu na to, čo bolo zvolené v paneli.
        // Návin sa ZÁMERNE nepredvypĺňa — nerobí sa na každom stĺpe.
        // Zadáva sa ručne v úprave kábla, aj dodatočne.
        const cableData: any = {
          from_pole_id: lastPole.id,
          to_pole_id: pole.id,
          cable_type: cableTypeFromFiberCount(fiberCount),
          // length_meters zámerne NEPOSIELAME: databázový trigger
          // calculate_cable_geometry() dopočíta dĺžku z GPS súradníc stĺpov,
          // ale iba ak je hodnota NULL. Kým sa sem posielala 0, trigger sa
          // preskočil a všetky takto vytvorené káble mali nulovú dĺžku.
          notes: `Auto-created via cable-by-click mode`,
        };

        // Add buffer_config only if it's defined
        if (bufferConfig) {
          cableData.buffer_config = bufferConfig;
        }

        const newCable = await useCableStore.getState().createCable(currentProject.id, cableData);
        console.log('✅ Cable created:', newCable.id);

        // Create/update cassettes if needed
        if (!cableByClickParams.jedenKabel && cableByClickParams.cassetteType !== 'none') {
          await createCassettesForCableChain(lastPole, pole, newCable);
        }

        // Add pole to chain
        setCableChain([...cableChain, pole]);
        setChainSteps([...chainSteps, {
          cableId: newCable.id, fromPoleId: lastPole.id, toPoleId: pole.id,
        }]);

        // Refresh data
        await fetchCables(currentProject.id);
        await fetchPoles(currentProject.id);

        console.log(`✅ Cable chain extended: ${cableChain.length + 1} poles`);
      }
    } catch (error: any) {
      console.error('❌ Error creating cable:', error);
      alert(getErrorMessage(error, 'Nepodarilo sa vytvoriť kábel.'));
    }
  };

  // Create cassettes for cable chain
  const createCassettesForCableChain = async (fromPole: Pole, toPole: Pole, cable: Cable) => {
    if (!currentProject) return;

    const { cassetteType } = cableByClickParams;

    try {
      // Only create cassettes on the first pole in the chain
      // For subsequent poles, cassettes will be created when the next cable is added
      if (cableChain.length === 0) {
        // This is the first cable - create cassette on fromPole
        await createOrUpdateCassetteOnPole(fromPole, null, cable.id);
      } else {
        // This is a subsequent cable - update cassette on fromPole (add output)
        const previousCable = cables.find(c => c.to_pole_id === fromPole.id);
        if (previousCable) {
          await createOrUpdateCassetteOnPole(fromPole, previousCable.id, cable.id);
        }
      }

      // Always create cassette on toPole (with input only for now)
      await createOrUpdateCassetteOnPole(toPole, cable.id, null);
    } catch (error) {
      console.error('❌ Error creating cassettes:', error);
      throw error;
    }
  };

  // Helper to create or update cassette on a pole
  const createOrUpdateCassetteOnPole = async (pole: Pole, inputCableId: number | null, outputCableId: number | null) => {
    if (!currentProject) return;

    const { fiberCount, cassetteType } = cableByClickParams;
    const existingCassette = pole.cassette || { has_cassette: false };

    if (cassetteType === 'passthrough') {
      // Create passthrough cassette
      const passThroughCables = existingCassette.pass_through_cables || [];

      // Only add if we have both input and output
      if (inputCableId && outputCableId) {
        // Create fiber mappings (1:1)
        const fibers = Array.from({ length: fiberCount }, (_, i) => ({
          fiber_number: i + 1,
          is_tapped: false,
        }));

        passThroughCables.push({
          from_cable_id: inputCableId,
          to_cable_id: outputCableId,
          fibers,
        });

        const cassette = {
          ...existingCassette,
          has_cassette: true,
          pass_through_cables: passThroughCables,
        };

        await updatePole(currentProject.id, pole.id, { cassette });
        console.log(`✅ Created/updated passthrough cassette on pole ${pole.name}`);
      }
    } else {
      // Create splitter cassette
      const splitters = existingCassette.splitters || [];

      const splitterRatio = cassetteType === 'splitter_1_2' ? 2 : cassetteType === 'splitter_1_4' ? 4 : 8;
      const splitterType = cassetteType.replace('splitter_', '') as '1:2' | '1:4' | '1:8';

      // Only create splitter if we have input cable
      if (inputCableId) {
        // Create splitter outputs
        const outputs = Array.from({ length: splitterRatio }, (_, i) => ({
          cable_id: i === 0 && outputCableId ? outputCableId : undefined,
          fiber: i === 0 && outputCableId ? 1 : undefined,
        }));

        splitters.push({
          type: splitterType,
          input_source_type: 'cable' as const,
          input_cable_id: inputCableId,
          input_fiber: 1,
          outputs,
        });

        const cassette = {
          ...existingCassette,
          has_cassette: true,
          splitters,
        };

        await updatePole(currentProject.id, pole.id, { cassette });
        console.log(`✅ Created/updated splitter cassette on pole ${pole.name}`);
      }
    }
  };

  const handleZlucKable = async (pole: Pole) => {
    setContextMenu(null);
    if (!currentProject || !canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    // Celý ťah naraz. Predtým sa po každom zlúčení pýtalo znovu cez window.confirm
    // a mobilné prehliadače druhé a ďalšie okno potlačia — reťaz sa ticho zastavila
    // a vyzeralo to, že susedný kábel sa zlúčiť nedá.
    const rada = najdiRaduNaZlucenie(pole);
    if (rada.length === 0) {
      alert(`Na stĺpe ${pole.name} sa nestretávajú práve dva káble s prázdnou kazetou.`);
      return;
    }

    const mena = rada.map((p) => p.name).join(', ');
    if (!window.confirm(
      rada.length === 1
        ? `Zlúčiť káble na stĺpe ${pole.name}?\n\nStane sa podporným stĺpom, `
          + 'kábel cezeň bude prechádzať neprerušený. Vlákna aj väzby v kazetách zostanú.'
        : `Zlúčiť celý ťah?\n\nPodpornými sa stanú stĺpy: ${mena}\n\n`
          + 'Z úsekov vznikne jeden kábel. Vlákna aj väzby v kazetách zostanú.')) {
      return;
    }

    let spojenych = 0;
    try {
      for (const p of rada) {
        const naStlpe = useCableStore.getState().cables.filter(
          (c) => c.from_pole_id === p.id || c.to_pole_id === p.id);
        if (naStlpe.length !== 2) break;
        const { kable } = zoradPodlaOlt(naStlpe, usePoleStore.getState().poles,
                                        useCableStore.getState().cables);
        await cablesAPI.mergeCables(currentProject.id, {
          pole_id: p.id,
          keep_cable_id: kable[0].id,
          remove_cable_id: kable[1].id,
        });
        await fetchCables(currentProject.id);
        spojenych++;
      }
      await fetchPoles(currentProject.id);
      alert(`✅ Zlúčené — ${spojenych} `
        + `${spojenych === 1 ? 'stĺp je' : spojenych < 5 ? 'stĺpy sú' : 'stĺpov je'} podporných.`);
    } catch (error: any) {
      await fetchCables(currentProject.id);
      await fetchPoles(currentProject.id);
      alert(getErrorMessage(error, 'Káble sa nepodarilo zlúčiť.')
        + (spojenych > 0 ? `\n\nPredtým sa podarilo zlúčiť ${spojenych}.` : ''));
    }
  };

  /**
   * Súvislá rada stĺpov, ktoré sa dajú zlúčiť — od zvoleného na obe strany.
   * Technik ťahá trasu po úsekoch, takže takých stĺpov býva za sebou viac.
   */
  const najdiRaduNaZlucenie = (start: Pole): Pole[] => {
    const zivePoles = poles;
    const zive = cables;
    const daSa = (p: Pole): boolean => {
      if (!p || p.is_virtual) return false;
      const kaz: any = p.cassette || {};
      if ((kaz.splitters || []).length || (kaz.splices || []).length
          || (kaz.pass_through_cables || []).length) return false;
      return zive.filter((c) => c.from_pole_id === p.id || c.to_pole_id === p.id).length === 2;
    };
    if (!daSa(start)) return [];

    const rada = [start];
    const videne = new Set([start.id]);
    // Postupuje po susedoch cez káble, kým stĺpy vyzerajú rovnako
    const kracaj = (odkial: Pole) => {
      let terajsi = odkial;
      for (;;) {
        const susedia = zive
          .filter((c) => c.from_pole_id === terajsi.id || c.to_pole_id === terajsi.id)
          .map((c) => (c.from_pole_id === terajsi.id ? c.to_pole_id : c.from_pole_id))
          .filter((id) => !videne.has(id));
        const dalsi = susedia
          .map((id) => zivePoles.find((p) => p.id === id))
          .find((p) => p && daSa(p));
        if (!dalsi) return;
        videne.add(dalsi.id);
        rada.push(dalsi);
        terajsi = dalsi;
      }
    };
    kracaj(start);
    return rada;
  };

  /**
   * Podporný stĺp, na ktorý pribudla krabica: kábel sa tam musí prerezať.
   * Kazeta v appke spája káble, ktoré na stĺpe KONČIA — kým cezeň len
   * prechádza, editor kazety tam nemá s čím pracovať.
   */
  const handleRozdelKabel = async (pole: Pole) => {
    setContextMenu(null);
    if (!currentProject || !canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    const cezenId = cables.filter((c) => (c.support_poles || []).includes(pole.id));
    if (cezenId.length !== 1) {
      alert(cezenId.length === 0
        ? `Cez stĺp ${pole.name} neprechádza žiadny kábel ako podporný.`
        : `Cez stĺp ${pole.name} prechádza káblov ${cezenId.length}. Rozdeľ ich po jednom z menu kábla.`);
      return;
    }
    const kabel = cezenId[0];

    if (!window.confirm(
      `Rozdeliť kábel #${kabel.id} na stĺpe ${pole.name}?\n\n`
      + 'Vzniknú z neho dva káble a na stĺpe kazeta s priechodom, ktorý ich spojí '
      + 'vlákno na vlákno — signál bude prechádzať ďalej presne ako teraz.\n\n'
      + 'Potom už len odbočíš vlákno, ktoré potrebuješ.')) {
      return;
    }

    try {
      // Pôvodné číslo si nechá polovica od OLT
      const odOlt = koniecOdOlt(kabel, poles, cables);
      const keepSide: 'from' | 'to' = odOlt === kabel.to_pole_id ? 'to' : 'from';
      const r: any = await cablesAPI.splitCable(currentProject.id, kabel.id, {
        pole_id: pole.id,
        keep_side: keepSide,
      });
      await fetchCables(currentProject.id);
      await fetchPoles(currentProject.id);
      const p1 = r.data?.povodny, p2 = r.data?.novy;
      alert(`✅ Rozdelené na stĺpe ${pole.name}.\n\n`
        + `Kábel #${p1?.id}: ${Math.round(p1?.length_meters || 0)} m\n`
        + `Kábel #${p2?.id}: ${Math.round(p2?.length_meters || 0)} m\n\n`
        + `V kazete vznikol priechod na ${r.data?.priechod_vlakien} vlákien.`);
    } catch (error: any) {
      alert(getErrorMessage(error, 'Kábel sa nepodarilo rozdeliť.'));
    }
  };

  const handlePoleRightClick = (pole: Pole, e: L.LeafletMouseEvent) => {
    e.originalEvent.preventDefault();
    setContextMenu({
      type: 'pole',
      pole,
      x: e.originalEvent.clientX,
      y: e.originalEvent.clientY,
    });
  };

  const handleCableRightClick = (cable: Cable, e: L.LeafletMouseEvent) => {
    e.originalEvent.preventDefault();
    setContextMenu({
      type: 'cable',
      cable,
      x: e.originalEvent.clientX,
      y: e.originalEvent.clientY,
    });
  };

  const handleMovePole = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setDraggingPole(pole);
    setContextMenu(null);
  };

  const handleEditPole = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedPole(pole);
    setEditPoleInitialTab('basic');
    setShowEditPoleDialog(true);
    setContextMenu(null);
  };

  const handleOpenPhotos = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedPole(pole);
    setEditPoleInitialTab('photos');
    setShowEditPoleDialog(true);
    setContextMenu(null);
  };

  const handleOpenCassetteEditor = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedPole(pole);
    setShowCassetteEditor(true);
    setContextMenu(null);
  };

  const handleOpenCustomerCassetteEditor = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    // Find virtual customer pole for this pole
    const customerPole = poles.find(p => p.parent_pole_id === pole.id && p.is_virtual);
    if (customerPole) {
      setSelectedPole(customerPole);
      setShowCassetteEditor(true);
    }
    setContextMenu(null);
  };

  const handleCreateCustomerCassette = async (pole: Pole) => {
    if (!currentProject) return;
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    try {
      await api.poles.createCustomerPole(currentProject.id, pole.id);
      // Reload poles and cables to get the new virtual pole and cable
      await fetchPoles(currentProject.id);
      await fetchCables(currentProject.id);
      alert('✅ Zákaznícka kazeta vytvorená');
    } catch (error: any) {
      alert(getErrorMessage(error, 'Nepodarilo sa vytvoriť zákaznícku kazetu.'));
    }
    setContextMenu(null);
  };

  const handleDeleteCustomerCassette = async (pole: Pole) => {
    if (!currentProject) return;
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    // Find virtual customer pole
    const customerPole = poles.find(p => p.parent_pole_id === pole.id && p.is_virtual);
    if (!customerPole) return;

    if (window.confirm(`Naozaj chcete vymazať zákaznícku kazetu pre ${pole.name}?`)) {
      await deletePole(currentProject.id, customerPole.id);
      await fetchPoles(currentProject.id);
      await fetchCables(currentProject.id);
    }
    setContextMenu(null);
  };

  const handleOpenOLTPortConfig = (pole: Pole) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedPole(pole);
    setShowOLTPortConfig(true);
    setContextMenu(null);
  };

  const handleOpenPoleNotes = (pole: Pole) => {
    setSelectedPole(pole);
    setShowPoleNotesDialog(true);
    setContextMenu(null);
  };

  const handleDeletePole = async (pole: Pole) => {
    if (!currentProject) return;

    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    // Show confirm dialog
    setPoleToDelete(pole);
    setShowConfirmDelete(true);
    setContextMenu(null);
  };

  const confirmDeletePole = async () => {
    if (!poleToDelete || !currentProject) return;

    try {
      // Call delete API - backend returns audit_log_id for undo
      const response = await api.poles.delete(currentProject.id, poleToDelete.id);
      const data = response.data as any;

      // Refresh data
      await fetchPoles(currentProject.id);
      await fetchCables(currentProject.id);
      await fetchONUs(currentProject.id);

      // Show undo toast
      if (data?.audit_log_id) {
        setUndoMessage(data.message || `Stĺpik "${poleToDelete.name}" vymazaný`);
        setUndoAuditLogId(data.audit_log_id);
        setShowUndoToast(true);
      }

      // Close dialog
      setShowConfirmDelete(false);
      setPoleToDelete(null);

    } catch (error: any) {
      console.error('Error deleting pole:', error);
      alert(getErrorMessage(error, 'Nepodarilo sa vymazať stĺpik.'));
    }
  };

  const handleUndoDelete = async () => {
    if (!undoAuditLogId || !currentProject) return;

    try {
      // Call undo API
      await api.poles.undo(currentProject.id, undoAuditLogId);

      // Refresh data
      await fetchPoles(currentProject.id);
      await fetchCables(currentProject.id);
      await fetchONUs(currentProject.id);

      // Close toast
      setShowUndoToast(false);
      setUndoAuditLogId(null);

      alert('✅ Stĺpik bol obnovený!');

    } catch (error: any) {
      console.error('Error undoing delete:', error);
      alert(getErrorMessage(error, 'Nepodarilo sa vrátiť zmenu.'));
    }
  };

  const handleEditCable = (cable: Cable) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedCable(cable);
    setShowAddCableDialog(true);
    setContextMenu(null);
  };

  const handleDeleteCable = async (cable: Cable) => {
    if (!currentProject) return;

    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }

    const fromPole = poles.find(p => p.id === cable.from_pole_id);
    const toPole = poles.find(p => p.id === cable.to_pole_id);
    const cableName = `${fromPole?.name || 'Neznámy'} → ${toPole?.name || 'Neznámy'}`;

    if (window.confirm(`Naozaj chcete vymazať kábel ${cableName}?`)) {
      try {
        await api.cables.delete(currentProject.id, cable.id);
        fetchCables(currentProject.id);
      } catch (error: any) {
        console.error('Error deleting cable:', error);
        alert(getErrorMessage(error, 'Nepodarilo sa vymazať kábel.'));
      }
    }
    setContextMenu(null);
  };

  // Cable-by-click panel handlers
  const handleCableByClickReset = () => {
    console.log('🔄 Cable chain reset - before:', cableChain.length);
    setCableChain([]);
    setChainSteps([]);
    console.log('🔄 Cable chain reset - after: 0');
  };

  const handleCableByClickFinish = async () => {
    // Pri voľbe „jeden súvislý kábel" sa úseky spoja až teraz. Zámerne až tu:
    // počas ťahania tak zostávajú samostatné a „Späť" ich vie zrušiť.
    if (cableByClickParams.jedenKabel && currentProject && chainSteps.length > 1) {
      const medzistlpy = cableChain.slice(1, -1);
      let zostava = chainSteps[0].cableId;
      let spojenych = 0;
      try {
        for (let i = 0; i < medzistlpy.length; i++) {
          const r: any = await cablesAPI.mergeCables(currentProject.id, {
            pole_id: medzistlpy[i].id,
            keep_cable_id: zostava,
            remove_cable_id: chainSteps[i + 1].cableId,
          });
          zostava = r.data?.cable_id ?? zostava;
          spojenych++;
        }
        await fetchCables(currentProject.id);
        await fetchPoles(currentProject.id);
        if (spojenych > 0) {
          alert(`✅ Trasa spojená do jedného kábla — ${spojenych} `
            + `${spojenych === 1 ? 'stĺp je' : spojenych < 5 ? 'stĺpy sú' : 'stĺpov je'} podporných.`);
        }
      } catch (error: any) {
        await fetchCables(currentProject.id);
        await fetchPoles(currentProject.id);
        alert(getErrorMessage(error,
          'Úseky sa nepodarilo spojiť do jedného kábla. Úseky zostali samostatné.'));
      }
    }

    setCableChain([]);
    setChainSteps([]);
    setCableByClickCollapsed(false);
    setAddCableByClickMode(false);
    console.log('✅ Cable-by-click mode finished');
  };

  /**
   * Odoberie z kazety všetko, čo odkazuje na daný kábel.
   * Kábel je čerstvo vytvorený, takže naň nemôže odkazovať nič staršie —
   * odstránenie je preto presné a nedotkne sa cudzích dát.
   * `has_cassette` nechávame tak, ako je: mohol ho niekto zapnúť zámerne
   * (krabica na stĺpe) a zhasnúť mu ho by bola strata informácie.
   */
  const kazetaBezKabla = (pole: Pole, cableId: number): any | null => {
    const kazeta: any = pole.cassette;
    if (!kazeta) return null;

    const povodnePriebezne: any[] = kazeta.pass_through_cables || [];
    const priebezne = povodnePriebezne
      .filter((pt) => pt.from_cable_id !== cableId && pt.to_cable_id !== cableId)
      // Vlákno môže byť odbočené do iného kábla (tap_to_cable_id). Keby zostalo
      // ukazovať na zmazaný kábel, v optickej ceste by visel mŕtvy odkaz.
      .map((pt) => ({
        ...pt,
        fibers: (pt.fibers || []).map((f: any) =>
          f && f.tap_to_cable_id === cableId
            ? { fiber_number: f.fiber_number, is_tapped: false }
            : f,
        ),
      }));

    const povodneSplittery: any[] = kazeta.splitters || [];
    const splittery = povodneSplittery
      .filter((sp) => sp.input_cable_id !== cableId)
      .map((sp) => ({
        ...sp,
        outputs: (sp.outputs || []).map((o: any) =>
          o && o.cable_id === cableId ? { ...o, cable_id: undefined, fiber: undefined } : o,
        ),
      }));

    const zmenilo =
      priebezne.length !== povodnePriebezne.length ||
      splittery.length !== povodneSplittery.length ||
      JSON.stringify(splittery) !== JSON.stringify(povodneSplittery);

    if (!zmenilo) return null;
    return { ...kazeta, pass_through_cables: priebezne, splitters: splittery };
  };

  /** Zruší posledný úsek reťazca — kábel aj to, čo sa naň naviazalo v kazetách. */
  const handleCableByClickUndo = async () => {
    if (!currentProject) return;

    // Reťazec má len začiatočný stĺpik, žiaden kábel ešte nevznikol
    if (chainSteps.length === 0) {
      setCableChain([]);
      return;
    }

    const krok = chainSteps[chainSteps.length - 1];
    const dotknute = poles.filter((p) => p.id === krok.fromPoleId || p.id === krok.toPoleId);

    // Poistka: ak medzitým niekto na splitter pripojil zákazníka, radšej nič nemažem
    const soZakaznikom = dotknute.some((p) =>
      ((p.cassette as any)?.splitters || []).some(
        (sp: any) =>
          sp.input_cable_id === krok.cableId &&
          (sp.outputs || []).some((o: any) => o?.customer_name),
      ),
    );
    if (soZakaznikom) {
      alert(
        'Na tomto úseku je už pripojený zákazník, tak ho radšej nerušim.\n'
        + 'Odpoj najprv zákazníka, alebo kábel zmaž ručne.',
      );
      return;
    }

    try {
      for (const p of dotknute) {
        const kazeta = kazetaBezKabla(p, krok.cableId);
        if (kazeta) {
          await updatePole(currentProject.id, p.id, { cassette: kazeta });
        }
      }
      await useCableStore.getState().deleteCable(currentProject.id, krok.cableId);

      setCableChain(cableChain.slice(0, -1));
      setChainSteps(chainSteps.slice(0, -1));

      await fetchCables(currentProject.id);
      await fetchPoles(currentProject.id);
      console.log('↩️ Posledný úsek zrušený, kábel', krok.cableId);
    } catch (error: any) {
      console.error('❌ Undo zlyhal:', error);
      alert(getErrorMessage(error, 'Posledný úsek sa nepodarilo zrušiť.'));
    }
  };

  // Delete cable mode handlers
  const handleToggleCableForDeletion = (cableId: number) => {
    console.log('🔘 Toggle cable for deletion:', cableId, 'Current selection:', selectedCablesForDeletion);
    setSelectedCablesForDeletion((prev) => {
      const newSelection = prev.includes(cableId)
        ? prev.filter((id) => id !== cableId)
        : [...prev, cableId];
      console.log('🔘 New selection:', newSelection);
      return newSelection;
    });
  };

  const removeCableFromCassettes = async (cableId: number) => {
    // Find all poles that use this cable in their cassette
    const affectedPoles = poles.filter(pole => {
      if (!pole.cassette) return false;
      const cassette = pole.cassette as any;

      // Check pass_through_cables
      if (cassette.pass_through_cables) {
        for (const pt of cassette.pass_through_cables) {
          if (pt.from_cable_id === cableId || pt.to_cable_id === cableId) return true;
          if (pt.fibers) {
            for (const fiber of pt.fibers) {
              if (fiber.tap_to_cable_id === cableId) return true;
            }
          }
        }
      }

      // Check splices
      if (cassette.splices) {
        for (const splice of cassette.splices) {
          if (splice.from_cable_id === cableId || splice.to_cable_id === cableId) return true;
        }
      }

      // Check splitters
      if (cassette.splitters) {
        for (const splitter of cassette.splitters) {
          if (splitter.input_cable_id === cableId) return true;
          if (splitter.outputs) {
            for (const output of splitter.outputs) {
              if (output.cable_id === cableId) return true;
            }
          }
        }
      }

      return false;
    });

    // Update each affected pole's cassette
    for (const pole of affectedPoles) {
      const cassette = JSON.parse(JSON.stringify(pole.cassette)); // Deep clone

      // Remove from pass_through_cables
      if (cassette.pass_through_cables) {
        cassette.pass_through_cables = cassette.pass_through_cables.filter((pt: any) => {
          if (pt.from_cable_id === cableId || pt.to_cable_id === cableId) return false;
          if (pt.fibers) {
            pt.fibers = pt.fibers.map((fiber: any) => {
              if (fiber.tap_to_cable_id === cableId) {
                return { ...fiber, is_tapped: false, tap_to_cable_id: null, tap_to_fiber: null };
              }
              return fiber;
            });
          }
          return true;
        });
      }

      // Remove from splices
      if (cassette.splices) {
        cassette.splices = cassette.splices.filter((splice: any) =>
          splice.from_cable_id !== cableId && splice.to_cable_id !== cableId
        );
      }

      // Remove from splitters
      if (cassette.splitters) {
        cassette.splitters = cassette.splitters.filter((splitter: any) => {
          if (splitter.input_cable_id === cableId) return false;
          if (splitter.outputs) {
            splitter.outputs = splitter.outputs.map((output: any) => {
              if (output.cable_id === cableId) {
                return { cable_id: null, fiber: null };
              }
              return output;
            });
          }
          return true;
        });
      }

      // Update pole cassette
      await api.poles.update(currentProject!.id, pole.id, { cassette });
      console.log(`✅ Updated cassette for pole ${pole.id} (removed cable ${cableId})`);
    }
  };

  const handleDeleteSelectedCables = async () => {
    if (!currentProject || selectedCablesForDeletion.length === 0) return;

    const confirmed = window.confirm(
      `Naozaj chceš vymazať ${selectedCablesForDeletion.length} káblov?\n\n` +
      `Toto vymaže aj všetky súvisiace:\n` +
      `- Passthrough prepojenia\n` +
      `- Zvary (splices)\n` +
      `- Splittre\n` +
      `- ONU pripojenia`
    );

    if (!confirmed) return;

    try {
      // First, remove cables from all cassettes
      for (const cableId of selectedCablesForDeletion) {
        await removeCableFromCassettes(cableId);
      }

      // Then delete each cable
      for (const cableId of selectedCablesForDeletion) {
        await useCableStore.getState().deleteCable(currentProject.id, cableId);
      }

      // Refresh data
      await fetchCables(currentProject.id);
      await fetchPoles(currentProject.id);
      await fetchONUs(currentProject.id);

      // Clear selection and exit delete mode
      setSelectedCablesForDeletion([]);
      setDeleteCableMode(false);

      console.log(`✅ Deleted ${selectedCablesForDeletion.length} cables`);
    } catch (error) {
      console.error('Failed to delete cables:', error);
      alert('Nepodarilo sa vymazať káble');
    }
  };

  const handleBufferSizeChange = (size: 12 | 24 | null) => {
    setCableByClickParams({ ...cableByClickParams, bufferSize: size, bufferColors: [] });
  };

  const handleBufferColorChange = (bufferIndex: number, color: string) => {
    const newColors = [...cableByClickParams.bufferColors];
    newColors[bufferIndex] = color;
    setCableByClickParams({ ...cableByClickParams, bufferColors: newColors });
  };

  // Klik na prázdnu mapu reťazec ZÁMERNE neruší. Predtým ho zrušil bez pýtania
  // a na telefóne sa technik netrafí do stĺpika ľahko — reťazec sa prerušil,
  // ďalší klik začal nový a v trase zostala diera, ktorú nebolo hneď vidieť.
  // Ruší sa len tlačidlom „Reset" v lište.
  const handleMapClickInCableMode = () => {
    // úmyselne nič
  };

  const handleMarkerDragEnd = async (pole: Pole, e: L.DragEndEvent) => {
    if (!currentProject) return;

    const marker = e.target;
    const position = marker.getLatLng();

    await updatePole(currentProject.id, pole.id, {
      latitude: position.lat,
      longitude: position.lng,
    });

    fetchPoles(currentProject.id);
    fetchCables(currentProject.id);

    setDraggingPole(null);
  };

  // ONU handlers
  const handleONUClick = (onu: ONUWithDetails) => {
    // Just show popup on click
  };

  const handleONURightClick = (onu: ONUWithDetails, e: L.LeafletMouseEvent) => {
    e.originalEvent.preventDefault();
    setContextMenu({
      type: 'onu',
      onu,
      x: e.originalEvent.clientX,
      y: e.originalEvent.clientY,
    });
  };

  const handleMoveONU = (onu: ONUWithDetails) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setDraggingONU(onu);
    setContextMenu(null);
  };

  const handleEditONU = (onu: ONUWithDetails) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    setSelectedONU(onu);
    setShowEditONUDialog(true);
    setContextMenu(null);
  };

  const handleOpenONUTechnicalReport = (onu: ONUWithDetails) => {
    setSelectedONU(onu);
    setShowONUTechnicalReport(true);
    setContextMenu(null);
  };

  const handleDeleteONU = async (onu: ONUWithDetails) => {
    if (!canEdit()) {
      alert('Nemáte oprávnenie na úpravu tohto projektu.');
      return;
    }
    if (!currentProject) return;

    const confirmed = window.confirm(
      `Naozaj chcete vymazať ONU "${onu.name}"?`
    );

    if (confirmed) {
      try {
        await useONUStore.getState().deleteONU(onu.id);
        fetchONUs(currentProject.id);
        setContextMenu(null);
      } catch (error) {
        console.error('Failed to delete ONU:', error);
        alert('Nepodarilo sa vymazať ONU');
      }
    }
    setContextMenu(null);
  };

  const handleONUDragEnd = async (onu: ONUWithDetails, e: L.DragEndEvent) => {
    if (!currentProject) return;

    const marker = e.target;
    const position = marker.getLatLng();

    try {
      await useONUStore.getState().updateONU(onu.id, {
        latitude: position.lat,
        longitude: position.lng,
      });

      fetchONUs(currentProject.id);
      setDraggingONU(null);
    } catch (error) {
      console.error('Failed to update ONU position:', error);
      alert('Nepodarilo sa aktualizovať pozíciu ONU');
      fetchONUs(currentProject.id); // Reload to reset position
      setDraggingONU(null);
    }
  };

  const getCableColor = (cable: Cable) => {
    // Vlastná farba kábla (úprava kábla → Farba na mape) ostáva aj pri výbere —
    // vybraný kábel sa potom len zhrubne. Bez vlastnej farby platí ako doteraz.
    if (cable.map_color) return cable.map_color;
    if (selectedCable?.id === cable.id) return '#667eea';
    return '#2c3e50';
  };

  // Súradnice stĺpa ako [lat, lng], alebo null keď ich nemá.
  const suradniceStlpa = (p?: Pole | null): [number, number] | null => {
    if (!p || p.latitude == null || p.longitude == null) return null;
    const lat = typeof p.latitude === 'string' ? parseFloat(p.latitude) : p.latitude;
    const lng = typeof p.longitude === 'string' ? parseFloat(p.longitude as string) : p.longitude;
    return [lat, lng];
  };

  const getCablePath = (cable: Cable): [number, number][] => {
    const fromPole = poles.find((p) => p.id === cable.from_pole_id);
    const toPole = poles.find((p) => p.id === cable.to_pole_id);

    if (!fromPole || !toPole || !fromPole.latitude || !toPole.latitude) {
      return [];
    }

    const fromLat = typeof fromPole.latitude === 'string'
      ? parseFloat(fromPole.latitude)
      : fromPole.latitude;
    const fromLng = typeof fromPole.longitude === 'string'
      ? parseFloat(fromPole.longitude as string)
      : fromPole.longitude!;
    const toLat = typeof toPole.latitude === 'string'
      ? parseFloat(toPole.latitude)
      : toPole.latitude;
    const toLng = typeof toPole.longitude === 'string'
      ? parseFloat(toPole.longitude as string)
      : toPole.longitude!;

    // Trasa vedie cez podporné stĺpy — kábel je na ne upnutý, nie prerušený.
    // Poradie určil server podľa polohy na trase, tu sa už len vykresľuje.
    const medzi = (cable.support_poles || [])
      .map((id) => suradniceStlpa(poles.find((p) => p.id === id)))
      .filter((x): x is [number, number] => x !== null);

    return [
      [fromLat, fromLng],
      ...medzi,
      [toLat, toLng],
    ];
  };

  if (!currentProject) {
    // Show project list if there are projects
    if (projects.length > 0) {
      return (
        <div className="map-view-empty project-selection-view">
          <div className="project-selection-header">
            <h2>📁 Vyberte projekt</h2>
            <p>Kliknite na projekt pre zobrazenie mapy</p>
          </div>
          <div className="project-selection-grid">
            {projects.map((project) => (
              <button
                key={project.id}
                className="project-selection-item"
                onClick={() => setCurrentProject(project)}
              >
                <div className="project-selection-icon">📁</div>
                <div className="project-selection-info">
                  <h3>{project.name}</h3>
                  {project.description && <p>{project.description}</p>}
                </div>
                <div className="project-selection-arrow">›</div>
              </button>
            ))}
          </div>
        </div>
      );
    }

    // No projects at all
    return (
      <div className="map-view-empty">
        <div className="empty-message">
          <span className="empty-icon">🗺️</span>
          <h3>Žiadne projekty</h3>
          <p>Začnite vytvorením projektu</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-view">
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        className="map-container"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapClickHandler onMapClick={handleMapClick} />
        <AutoFitBounds poles={poles} />
        <ZoomToTarget />

        {/* Render cables */}
        {cables.map((cable) => {
          // Skip virtual cables (connecting to customer cassettes) unless showVirtualPoles is enabled
          if (cable.is_virtual && !showVirtualPoles) return null;

          const path = getCablePath(cable);
          if (path.length === 0) return null;

          const isSelectedForDeletion = selectedCablesForDeletion.includes(cable.id);
          const cableColor = deleteCableMode && isSelectedForDeletion ? '#ef4444' : getCableColor(cable);
          // Kábel s vlastnou farbou je hrubší a sýtejší, aby bola farba vidno
          const maFarbu = !!cable.map_color;
          const cableWeight = deleteCableMode && isSelectedForDeletion ? 5
            : selectedCable?.id === cable.id ? (maFarbu ? 7 : 4)
            : maFarbu ? 4 : 2;

          // Debug log for selected cables
          if (isSelectedForDeletion) {
            console.log('🎨 Selected cable rendering:', {
              cableId: cable.id,
              deleteCableMode,
              isSelectedForDeletion,
              cableColor,
              cableWeight
            });
          }

          // Force re-render by changing key when selection changes
          const cableKey = `cable-${cable.id}-${isSelectedForDeletion ? 'selected' : 'normal'}`;

          return (
            <Polyline
              key={cableKey}
              positions={path}
              // pathOptions, nie color/weight: tie si react-leaflet vezme len pri
              // vzniku čiary, takže zmena farby či výber by sa neprejavili.
              pathOptions={{
                color: cableColor,
                weight: cableWeight,
                opacity: deleteCableMode && isSelectedForDeletion ? 0.9 : maFarbu ? 0.9 : 0.7,
              }}
              eventHandlers={{
                click: () => {
                  if (deleteCableMode) {
                    handleToggleCableForDeletion(cable.id);
                  } else {
                    setSelectedCable(cable);
                  }
                },
                contextmenu: (e) => handleCableRightClick(cable, e),
              }}
            >
              {!deleteCableMode && (
                <Popup>
                  {currentProject && (
                    <CablePopup
                      cable={cable}
                      projectId={currentProject.id}
                      onViewDetails={() => {
                        setFiberUsageCableId(cable.id);
                        setShowFiberUsageDialog(true);
                      }}
                    />
                  )}
                </Popup>
              )}
            </Polyline>
          );
        })}

        {/* Render ONU connections (cables from splitter to ONU) */}
        {showONUs && onus.filter(onu => onu.visible !== false).map((onu) => {
          if (!onu.latitude || !onu.longitude || !onu.pole_id) return null;

          // Find the pole this ONU is connected to
          const pole = poles.find(p => p.id === onu.pole_id);
          if (!pole || !pole.latitude || !pole.longitude) return null;

          const onuLat = typeof onu.latitude === 'string' ? parseFloat(onu.latitude) : onu.latitude;
          const onuLng = typeof onu.longitude === 'string' ? parseFloat(onu.longitude) : onu.longitude;
          const poleLat = typeof pole.latitude === 'string' ? parseFloat(pole.latitude) : pole.latitude;
          const poleLng = typeof pole.longitude === 'string' ? parseFloat(pole.longitude) : pole.longitude;

          return (
            <Polyline
              key={`onu-connection-${onu.id}`}
              positions={[
                [poleLat, poleLng],
                [onuLat, onuLng]
              ]}
              pathOptions={{
                color: '#10b981',  // Green color for ONU connections
                weight: 2,
                opacity: 0.6,
                dashArray: '5, 5'  // Dashed line to distinguish from regular cables
              }}
            >
              <Popup>
                <div className="cable-popup">
                  <h4>📡 ONU Pripojenie</h4>
                  <p><strong>ONU:</strong> {onu.name}</p>
                  <p><strong>Stĺp:</strong> {pole.name}</p>
                  {onu.splitter_index !== undefined && onu.splitter_port !== undefined && (
                    <p><strong>Port:</strong> Splitter {onu.splitter_index + 1}, Port {onu.splitter_port}</p>
                  )}
                </div>
              </Popup>
            </Polyline>
          );
        })}

        {/* Render poles */}
        {poles.map((pole) => {
          // Skip virtual poles (customer cassettes) unless showVirtualPoles is enabled
          if (pole.is_virtual && !showVirtualPoles) return null;

          if (!pole.latitude || !pole.longitude) return null;

          const lat = typeof pole.latitude === 'string'
            ? parseFloat(pole.latitude)
            : pole.latitude;
          const lng = typeof pole.longitude === 'string'
            ? parseFloat(pole.longitude)
            : pole.longitude;

          const position: [number, number] = [lat, lng];

          // Check if this pole is the first in cable chain
          const isFirstInChain = addCableByClickMode && cableChain.length > 0 && cableChain[0].id === pole.id;

          return (
            <Marker
              key={pole.id}
              position={position}
              icon={createPoleIcon(pole, poles, isFirstInChain,
                SIGNAL_FARBY[(signalPodlaStlpa.get(pole.id)?.stav ?? 'ziadny') as SignalStav])}
              draggable={draggingPole?.id === pole.id}
              eventHandlers={{
                click: () => handlePoleClick(pole),
                contextmenu: (e) => handlePoleRightClick(pole, e),
                dragend: (e) => handleMarkerDragEnd(pole, e),
              }}
            >
              <Tooltip permanent direction="right"
                offset={jePodporny(pole) ? [6, 0] : [15, 0]}
                className={jePodporny(pole) ? 'pole-label pole-label-podporny' : 'pole-label'}>
                <div>
                  <div>{pole.name}</div>
                  {pole.show_map_note && pole.map_note && (
                    <div style={{ fontSize: '11px', color: '#e0e0e0', marginTop: '4px', fontStyle: 'italic' }}>
                      {pole.map_note}
                    </div>
                  )}
                </div>
              </Tooltip>

              <Popup>
                <div className="pole-popup">
                  <h4>{pole.name}</h4>
                  <p>Type: {pole.pole_type}</p>
                  {pole.splitter_type && <p>Splitter: {pole.splitter_type}</p>}
                  {pole.pole_type === 'olt' && pole.olt_port_count && pole.olt_port_count > 0 && (
                    <p>🔌 Porty: {pole.olt_port_count}</p>
                  )}
                  {pole.cassette && pole.cassette.has_cassette && (
                    <div>
                      <p>📦 Kazeta:</p>
                      {pole.cassette.splices && pole.cassette.splices.length > 0 && (
                        <p style={{ marginLeft: '10px', fontSize: '12px' }}>
                          • {pole.cassette.splices.length} zvarov
                        </p>
                      )}
                      {pole.cassette.splitters && pole.cassette.splitters.length > 0 && (
                        <p style={{ marginLeft: '10px', fontSize: '12px' }}>
                          • {pole.cassette.splitters.length} splittrov
                        </p>
                      )}
                    </div>
                  )}
                  {pole.endpoint_type !== 'none' && (
                    <p>
                      {pole.endpoint_type === 'building'
                        ? `${pole.customer_count} customers`
                        : 'Single customer'}
                    </p>
                  )}
                  <p className="coordinates">
                    {pole.latitude}, {pole.longitude}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}

        {/* ONU Markers */}
        {showONUs && onus.filter(onu => onu.visible !== false).map((onu) => {
          if (!onu.latitude || !onu.longitude) return null;

          const lat = typeof onu.latitude === 'string' ? parseFloat(onu.latitude) : onu.latitude;
          const lng = typeof onu.longitude === 'string' ? parseFloat(onu.longitude) : onu.longitude;

          return (
            <Marker
              key={`onu-${onu.id}`}
              position={[lat, lng]}
              icon={createONUIcon(onu)}
              draggable={draggingONU?.id === onu.id}
              eventHandlers={{
                click: () => handleONUClick(onu),
                contextmenu: (e) => handleONURightClick(onu, e),
                dragend: (e) => handleONUDragEnd(onu, e),
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} opacity={0.9}>
                <div style={{ fontSize: '12px', fontWeight: 500 }}>
                  📡 {onu.name}
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    Stav: {onu.status}
                  </div>
                </div>
              </Tooltip>

              <Popup>
                <div className="pole-popup">
                  <h4>📡 {onu.name}</h4>
                  <p><strong>Stav:</strong> {onu.status}</p>
                  {onu.onu_model && <p><strong>Model:</strong> {onu.onu_model}</p>}
                  {onu.serial_number && <p><strong>S/N:</strong> {onu.serial_number}</p>}
                  {onu.mac_address && <p><strong>MAC:</strong> {onu.mac_address}</p>}
                  {onu.pole_name && <p><strong>Stĺp:</strong> {onu.pole_name}</p>}
                  {onu.splitter_index !== undefined && onu.splitter_port !== undefined && (
                    <p><strong>Port:</strong> Splitter {onu.splitter_index + 1}, Port {onu.splitter_port}</p>
                  )}
                  {onu.customer_name && <p><strong>Zákazník:</strong> {onu.customer_name}</p>}
                  {onu.fiber_color && <p><strong>Farba vlákna:</strong> {onu.fiber_color}</p>}
                  {onu.signal_strength && <p><strong>Signál:</strong> {onu.signal_strength} dBm</p>}
                  <p className="coordinates">
                    {lat.toFixed(6)}, {lng.toFixed(6)}
                  </p>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Map legend - Collapsible */}
      <div className={`map-legend ${legendExpanded ? 'expanded' : 'collapsed'}`}>
        <button 
          className="legend-toggle"
          onClick={() => setLegendExpanded(!legendExpanded)}
          title={legendExpanded ? 'Skryť legendu' : 'Zobraziť legendu'}
        >
          <span className="legend-toggle-icon">{legendExpanded ? '✕' : 'ℹ️'}</span>
          {legendExpanded && <span className="legend-toggle-text">Legenda</span>}
        </button>
        {legendExpanded && (
          <div className="legend-content">
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#e74c3c' }}></div>
              <span>OLT</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#f39c12' }}></div>
              <span>Splitter</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#3498db' }}></div>
              <span>Budova</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#95a5a6' }}></div>
              <span>Stĺpik</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#10b981', width: '12px', height: '12px', borderRadius: '2px' }}></div>
              <span>ONU (aktívne)</span>
            </div>
            <div className="legend-item">
              <div className="legend-icon" style={{ backgroundColor: '#f59e0b', width: '12px', height: '12px', borderRadius: '2px' }}></div>
              <span>ONU (čakajúce)</span>
            </div>
            <div className="legend-item">
              <div className="legend-line"></div>
              <span>Kábel</span>
            </div>
          </div>
        )}
      </div>

      {/* Map controls menu */}
      <MapControlsMenu
        addPoleMode={addPoleMode}
        showONUs={showONUs}
        showVirtualPoles={showVirtualPoles}
        addCableByClickMode={addCableByClickMode}
        deleteCableMode={deleteCableMode}
        selectedCablesCount={selectedCablesForDeletion.length}
        onToggleAddPoleMode={toggleAddPoleMode}
        onToggleShowONUs={toggleShowONUs}
        onToggleShowVirtualPoles={toggleShowVirtualPoles}
        showSignal={showSignal}
        onToggleShowSignal={toggleShowSignal}
        onToggleAddCableByClickMode={toggleAddCableByClickMode}
        onToggleDeleteCableMode={toggleDeleteCableMode}
        onDeleteSelectedCables={handleDeleteSelectedCables}
        onRecalculateColors={handleRecalculateColors}
        onAddCable={() => setShowAddCableDialog(true)}
        onImportPoles={() => setShowImportDialog(true)}
        onExportPoles={() => setShowExportDialog(true)}
        onShowStatistics={() => setShowProjectFiberUsageDialog(true)}
      />

      {/* Cable-by-click panel */}
      {showSignal && (
        <SignalLegend
          pocty={pocetyStavov}
          prahZelena={prahy(currentProject?.settings).zelena}
          prahOranzova={prahy(currentProject?.settings).oranzova}
        />
      )}

      <AddCableByClickPanel
        isActive={addCableByClickMode}
        collapsed={cableByClickCollapsed}
        fiberCount={cableByClickParams.fiberCount}
        cableType={cableByClickParams.cableType}
        cassetteType={cableByClickParams.cassetteType}
        bufferSize={cableByClickParams.bufferSize}
        bufferColors={cableByClickParams.bufferColors}
        jedenKabel={cableByClickParams.jedenKabel}
        onJedenKabelChange={(v) => setCableByClickParams({ ...cableByClickParams, jedenKabel: v })}
        chainLength={cableChain.length}
        onFiberCountChange={(count) => setCableByClickParams({ ...cableByClickParams, fiberCount: count, bufferColors: [] })}
        onCableTypeChange={(type) => setCableByClickParams({ ...cableByClickParams, cableType: type })}
        onCassetteTypeChange={(type) => setCableByClickParams({ ...cableByClickParams, cassetteType: type })}
        onBufferSizeChange={handleBufferSizeChange}
        onBufferColorChange={handleBufferColorChange}
        onReset={handleCableByClickReset}
        onFinish={handleCableByClickFinish}
        onClose={() => { handleCableByClickReset(); setCableByClickCollapsed(false); setAddCableByClickMode(false); }}
        onStart={() => setCableByClickCollapsed(true)}
        onExpand={() => setCableByClickCollapsed(false)}
        onUndo={handleCableByClickUndo}
        canUndo={chainSteps.length > 0 || cableChain.length > 0}
      />

      {/* Context Menu */}
      {contextMenu && contextMenu.type === 'pole' && contextMenu.pole && (
        <PlavajuceMenu className="pole-context-menu" x={contextMenu.x} y={contextMenu.y}>
          <div className="context-menu-header">
            {contextMenu.pole.name}
          </div>
          {canEdit() && (
            <button
              className="context-menu-item"
              onClick={() => {
                setNavinPoleId(contextMenu.pole!.id);
                setContextMenu(null);
              }}
            >
              <span>🧵</span> Návin na tomto stĺpe
            </button>
          )}
          {/* Poznámky - dostupné len ak má používateľ modul pole_notes */}
          {hasModule('pole_notes') && (
            <button
              className="context-menu-item"
              onClick={() => handleOpenPoleNotes(contextMenu.pole!)}
            >
              <span>📝</span> Poznámky ku stĺpiku
            </button>
          )}

          {canEdit() && cables.filter(
            (c) => (c.support_poles || []).includes(contextMenu.pole!.id)).length === 1
            && cables.filter((c) => c.from_pole_id === contextMenu.pole!.id
                                 || c.to_pole_id === contextMenu.pole!.id).length === 0 && (
            <button
              className="context-menu-item"
              onClick={() => handleRozdelKabel(contextMenu.pole!)}
              title="Prereže kábel na tomto stĺpe, aby sa dala pridať kazeta"
            >
              <span>✂️</span> Rozdeliť kábel (pridať kazetu)
            </button>
          )}

          {canEdit() && cables.filter(
            (c) => c.from_pole_id === contextMenu.pole!.id
                || c.to_pole_id === contextMenu.pole!.id).length === 2 && (
            <button
              className="context-menu-item"
              onClick={() => handleZlucKable(contextMenu.pole!)}
              title="Spojí dva káble do jedného, stĺp sa stane podporným"
            >
              <span>🪢</span> Zlúčiť káble (podporný stĺp)
            </button>
          )}
          {canEdit() && (
            <>
              <button
                className="context-menu-item"
                onClick={() => handleMovePole(contextMenu.pole!)}
              >
                <span>🔄</span> Presunúť
              </button>
              <button
                className="context-menu-item"
                onClick={() => handleEditPole(contextMenu.pole!)}
              >
                <span>✏️</span> Upraviť
              </button>
            </>
          )}
          {canEdit() && hasModule('pole_photos', 'edit') && (
            <button
              className="context-menu-item"
              onClick={() => handleOpenPhotos(contextMenu.pole!)}
            >
              <span>📸</span> Fotky
            </button>
          )}
          {canEdit() && hasModule('cassette_editor', 'edit') && (
            <button
              className="context-menu-item"
              onClick={() => handleOpenCassetteEditor(contextMenu.pole!)}
            >
              <span>📦</span> Editor kazety
            </button>
          )}
          {canEdit() && hasModule('cassette_editor', 'edit') && !contextMenu.pole.is_virtual && (
            <>
              {poles.find(p => p.parent_pole_id === contextMenu.pole!.id && p.is_virtual) ? (
                <>
                  <button
                    className="context-menu-item"
                    onClick={() => handleOpenCustomerCassetteEditor(contextMenu.pole!)}
                  >
                    <span>👥</span> Editor zákazníckej kazety
                  </button>
                  <button
                    className="context-menu-item delete"
                    onClick={() => handleDeleteCustomerCassette(contextMenu.pole!)}
                  >
                    <span>🗑️</span> Vymazať zákaznícku kazetu
                  </button>
                </>
              ) : (
                <button
                  className="context-menu-item"
                  onClick={() => handleCreateCustomerCassette(contextMenu.pole!)}
                >
                  <span>➕</span> Vytvoriť zákaznícku kazetu
                </button>
              )}
            </>
          )}
          {canEdit() && hasModule('olt_config', 'edit') && contextMenu.pole.pole_type === 'olt' && (
            <button
              className="context-menu-item"
              onClick={() => handleOpenOLTPortConfig(contextMenu.pole!)}
            >
              <span>🔌</span> OLT porty
            </button>
          )}
          {canEdit() && (
            <button
              className="context-menu-item delete"
              onClick={() => handleDeletePole(contextMenu.pole!)}
            >
              <span>🗑️</span> Vymazať
            </button>
          )}
        </PlavajuceMenu>
      )}

      {/* Cable Context Menu */}
      {/* Čaká sa na klik na podporný stĺp */}
      {uchytitNaStlpCableId !== null && (
        <div
          style={{
            position: 'fixed',
            top: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            background: '#1e3a5f',
            color: '#dbeafe',
            border: '1px solid #3b82f6',
            borderRadius: '8px',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            fontSize: '14px',
          }}
        >
          <span>
            🔗 Klikni na stĺp, na ktorý sa kábel #{uchytitNaStlpCableId} uchytí.
            Kábel sa neprerušuje — len povedie cezeň.
          </span>
          <button
            onClick={() => setUchytitNaStlpCableId(null)}
            style={{
              background: '#334155', color: '#e2e8f0', border: 'none',
              borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '13px',
            }}
          >
            Zrušiť (Esc)
          </button>
        </div>
      )}

      {contextMenu && contextMenu.type === 'cable' && contextMenu.cable && (
        <PlavajuceMenu className="pole-context-menu" x={contextMenu.x} y={contextMenu.y}>
          <div className="context-menu-header">
            Kábel #{contextMenu.cable.id}
          </div>
          <button
            className="context-menu-item"
            onClick={() => {
              setFiberUsageCableId(contextMenu.cable!.id);
              setShowFiberUsageDialog(true);
              setContextMenu(null);
            }}
          >
            <span>📊</span> Využitie vlákien
          </button>
          {canEdit() && (
            <button
              className="context-menu-item"
              onClick={() => {
                setUchytitNaStlpCableId(contextMenu.cable!.id);
                setContextMenu(null);
              }}
            >
              <span>🔗</span> Uchytiť na podporný stĺp
            </button>
          )}
          {canEdit() ? (
            <>
              <button
                className="context-menu-item"
                onClick={() => handleEditCable(contextMenu.cable!)}
              >
                <span>✏️</span> Upraviť
              </button>
              <button
                className="context-menu-item"
                onClick={() => {
                  handleToggleCableForDeletion(contextMenu.cable!.id);
                  if (!deleteCableMode) {
                    setDeleteCableMode(true);
                  }
                  setContextMenu(null);
                }}
                style={{
                  backgroundColor: selectedCablesForDeletion.includes(contextMenu.cable!.id) ? '#fef3c7' : undefined
                }}
              >
                <span>✅</span> {selectedCablesForDeletion.includes(contextMenu.cable!.id) ? 'Odznačiť' : 'Označiť na vymazanie'}
              </button>
              <button
                className="context-menu-item delete"
                onClick={() => handleDeleteCable(contextMenu.cable!)}
              >
                <span>🗑️</span> Vymazať tento kábel
              </button>
            </>
          ) : (
            <div style={{ padding: '12px', color: '#999', fontSize: '13px' }}>
              Iba na čítanie
            </div>
          )}
        </PlavajuceMenu>
      )}

      {/* ONU Context Menu */}
      {contextMenu && contextMenu.type === 'onu' && contextMenu.onu && (
        <PlavajuceMenu className="pole-context-menu" x={contextMenu.x} y={contextMenu.y}>
          <div className="context-menu-header">
            📡 {contextMenu.onu.name}
          </div>
          {/* technical_reports nemá vlastný endpoint - správa sa skladá z dát,
              ktoré používateľ aj tak číta. Modul tu teda iba schová vstup,
              nie je to ochrana API. */}
          {hasModule('technical_reports') && (
            <button
              className="context-menu-item"
              onClick={() => handleOpenONUTechnicalReport(contextMenu.onu!)}
            >
              <span>📋</span> Technická správa
            </button>
          )}
          {canEdit() ? (
            <>
              <button
                className="context-menu-item"
                onClick={() => handleMoveONU(contextMenu.onu!)}
              >
                <span>🔄</span> Presunúť
              </button>
              <button
                className="context-menu-item"
                onClick={() => handleEditONU(contextMenu.onu!)}
              >
                <span>✏️</span> Upraviť
              </button>
              <button
                className="context-menu-item delete"
                onClick={() => handleDeleteONU(contextMenu.onu!)}
              >
                <span>🗑️</span> Vymazať
              </button>
            </>
          ) : (
            <div style={{ padding: '12px', color: '#999', fontSize: '13px' }}>
              Iba na čítanie
            </div>
          )}
        </PlavajuceMenu>
      )}

      {/* Dialogs */}
      {clickedPosition && (
        <AddPoleDialog
          isOpen={showAddPoleDialog}
          onClose={() => {
            setShowAddPoleDialog(false);
            setClickedPosition(null);
          }}
          latitude={clickedPosition[0]}
          longitude={clickedPosition[1]}
        />
      )}

      {selectedPole && (
        <EditPoleDialog
          isOpen={showEditPoleDialog}
          onClose={() => {
            setShowEditPoleDialog(false);
            setSelectedPole(null);
            setEditPoleInitialTab('basic');
          }}
          pole={selectedPole}
          initialTab={editPoleInitialTab}
        />
      )}

      {selectedPole && (
        <CassetteEditorDialog
          isOpen={showCassetteEditor}
          onClose={() => {
            setShowCassetteEditor(false);
            setSelectedPole(null);
          }}
          pole={selectedPole}
        />
      )}

      {selectedPole && (
        <OLTPortConfigDialog
          isOpen={showOLTPortConfig}
          onClose={() => {
            setShowOLTPortConfig(false);
            setSelectedPole(null);
          }}
          pole={selectedPole}
        />
      )}

      <PoleReserveDialog
        isOpen={navinPoleId !== null}
        onClose={() => setNavinPoleId(null)}
        projectId={currentProject.id}
        poleId={navinPoleId}
        onSaved={() => fetchCables(currentProject.id)}
      />

      <AddCableDialog
        isOpen={showAddCableDialog}
        onClose={() => {
          setShowAddCableDialog(false);
          setSelectedCable(null);
        }}
        cable={selectedCable}
      />

      {selectedPole && (
        <TechnicalReportDialog
          isOpen={showTechnicalReport}
          onClose={() => {
            setShowTechnicalReport(false);
            setSelectedPole(null);
          }}
          pole={selectedPole}
          cables={cables}
          poles={poles}
        />
      )}

      {selectedPole && currentProject && showInstallationTechDialog && (
        <InstallationTechDialog
          pole={selectedPole}
          projectId={currentProject.id}
          onClose={() => {
            setShowInstallationTechDialog(false);
            setSelectedPole(null);
          }}
          onCustomerConnected={() => {
            // Refresh poles to get updated customer data
            fetchPoles(currentProject.id);
          }}
        />
      )}

      {selectedONU && (
        <EditONUDialog
          isOpen={showEditONUDialog}
          onClose={() => {
            setShowEditONUDialog(false);
            setSelectedONU(null);
          }}
          onu={selectedONU}
        />
      )}

      {selectedONU && (
        <ONUTechnicalReportDialog
          isOpen={showONUTechnicalReport}
          onClose={() => {
            setShowONUTechnicalReport(false);
            setSelectedONU(null);
          }}
          onu={selectedONU}
          pole={poles.find(p => p.id === selectedONU.pole_id)}
        />
      )}

      <ImportPolesDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        projectId={currentProject.id}
        onImported={() => fetchPoles(currentProject.id)}
      />

      <ExportPolesDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        poles={poles}
        nazovProjektu={currentProject.name}
      />

      {currentProject && fiberUsageCableId && (
        <CableFiberUsageDialog
          isOpen={showFiberUsageDialog}
          onClose={() => {
            setShowFiberUsageDialog(false);
            setFiberUsageCableId(null);
          }}
          projectId={currentProject.id}
          cableId={fiberUsageCableId}
        />
      )}

      {currentProject && (
        <ProjectFiberUsageDialog
          isOpen={showProjectFiberUsageDialog}
          onClose={() => setShowProjectFiberUsageDialog(false)}
          projectId={currentProject.id}
        />
      )}

      {/* Pole Notes Dialog */}
      {selectedPole && currentProject && (
        <PoleNotesDialog
          isOpen={showPoleNotesDialog}
          onClose={() => setShowPoleNotesDialog(false)}
          poleId={selectedPole.id}
          poleName={selectedPole.name}
          projectId={currentProject.id}
        />
      )}

      {/* Confirm Delete Dialog */}
      {poleToDelete && (
        <ConfirmDeleteDialog
          isOpen={showConfirmDelete}
          onClose={() => {
            setShowConfirmDelete(false);
            setPoleToDelete(null);
          }}
          onConfirm={confirmDeletePole}
          poleName={poleToDelete.name}
          hasCassette={poleToDelete.cassette?.has_cassette || false}
          splicesCount={poleToDelete.cassette?.splices?.length || 0}
          splittersCount={poleToDelete.cassette?.splitters?.length || 0}
          cablesCount={cables.filter(c => c.from_pole_id === poleToDelete.id || c.to_pole_id === poleToDelete.id).length}
          onusCount={onus.filter(o => o.pole_id === poleToDelete.id).length}
        />
      )}

      {/* Undo Toast */}
      {showUndoToast && (
        <UndoToast
          message={undoMessage}
          onUndo={handleUndoDelete}
          onClose={() => {
            setShowUndoToast(false);
            setUndoAuditLogId(null);
          }}
          duration={10}
        />
      )}
    </div>
  );
};
